import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Loader2, Plus, Search, Upload, FolderOpen, UserPlus, Download, Edit, X, Phone, Mail, CheckCircle, MoreHorizontal, Send, ShieldCheck } from 'lucide-react';
import { AdminPageShell, SortIcon, createSortHelpers } from '../components/AdminShared';
import { toast } from 'sonner';

export default function AdminClientsPage() {
  const [searchParams] = useSearchParams();
  const filterOrgId = searchParams.get('org_id') || '';
  const filterGroupId = searchParams.get('client_group_id') || '';

  const [clients, setClients] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState(filterOrgId);
  const [selectedGroups, setSelectedGroups] = useState(filterGroupId ? [filterGroupId] : []);
  const [groupSearch, setGroupSearch] = useState('');
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  // Add/Edit client
  const [showAdd, setShowAdd] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [addForm, setAddForm] = useState({ email: '', password: '', name: '', phone: '', org_id: '', client_group_id: '' });
  const [addLoading, setAddLoading] = useState(false);
  // Bulk upload
  const [showBulk, setShowBulk] = useState(false);
  const [bulkOrgId, setBulkOrgId] = useState('');
  const [bulkGroupId, setBulkGroupId] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  // Add group
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [groupForm, setGroupForm] = useState({ org_id: '', name: '', description: '' });

  const loadAll = useCallback(async () => {
    try {
      const [cRes, oRes, gRes] = await Promise.all([
        adminAPI.listClients({ org_id: filterOrgId, client_group_id: filterGroupId }),
        adminAPI.listOrgs(),
        adminAPI.listClientGroups({})
      ]);
      setClients(cRes.data);
      setOrgs(oRes.data.sort((a, b) => a.name.localeCompare(b.name)));
      setGroups(gRes.data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterOrgId, filterGroupId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadClients = useCallback(async () => {
    try {
      const res = await adminAPI.listClients({ org_id: orgFilter, client_group_id: selectedGroups.length === 1 ? selectedGroups[0] : '' });
      setClients(res.data);
    } catch (e) { console.error(e); }
  }, [orgFilter, selectedGroups]);

  useEffect(() => { if (!loading) loadClients(); }, [loading, loadClients]);

  const { toggleSort } = createSortHelpers(sortCol, sortDir, setSortCol, setSortDir);

  const filtered = useMemo(() => {
    let list = clients.filter(c => {
      const matchSearch = !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase()) || c.org_name?.toLowerCase().includes(search.toLowerCase());
      const matchGroups = selectedGroups.length === 0 || selectedGroups.includes(c.client_group_id);
      return matchSearch && matchGroups;
    });
    if (sortCol) {
      list = [...list].sort((a, b) => {
        let va = '', vb = '';
        if (sortCol === 'name') { va = a.name || ''; vb = b.name || ''; }
        else if (sortCol === 'email') { va = a.email || ''; vb = b.email || ''; }
        else if (sortCol === 'org') { va = a.org_name || ''; vb = b.org_name || ''; }
        else if (sortCol === 'group') { va = a.client_group_name || ''; vb = b.client_group_name || ''; }
        else if (sortCol === 'agreement') { va = a.agreement_accepted ? '1' : '0'; vb = b.agreement_accepted ? '1' : '0'; }
        else if (sortCol === 'joined') { va = a.created_at || ''; vb = b.created_at || ''; }
        const cmp = va.localeCompare(vb);
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return list;
  }, [clients, search, selectedGroups, sortCol, sortDir]);

  const openEdit = (c) => {
    setEditClient(c);
    setAddForm({ email: c.email, password: '', name: c.name, phone: c.phone || '', org_id: c.org_id, client_group_id: c.client_group_id || '' });
    setShowAdd(true);
  };

  const openCreate = () => {
    setEditClient(null);
    setAddForm({ email: '', password: '', name: '', phone: '', org_id: '', client_group_id: '' });
    setShowAdd(true);
  };

  const handleSubmit = async () => {
    setAddLoading(true);
    try {
      if (editClient) {
        await adminAPI.updateClient(editClient.id, { name: addForm.name, email: addForm.email, phone: addForm.phone, org_id: addForm.org_id, client_group_id: addForm.client_group_id });
      } else {
        await adminAPI.createClient(addForm);
      }
      setShowAdd(false);
      toast.success(editClient ? 'Client updated' : 'Client created — validation email sent');
      loadClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setAddLoading(false);
  };

  const handleSendValidation = async (clientId) => {
    try {
      await adminAPI.sendClientValidation(clientId);
      toast.success('Validation email sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send validation email');
    }
  };

  const handleManualValidate = async (clientId) => {
    try {
      await adminAPI.validateClientEmail(clientId);
      toast.success('Email marked as validated');
      loadClients();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !bulkOrgId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('org_id', bulkOrgId);
      formData.append('client_group_id', bulkGroupId);
      const res = await adminAPI.bulkUploadClients(formData);
      setBulkResult(res.data);
      loadClients();
    } catch (err) { console.error(err); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleAddGroup = async () => {
    try {
      await adminAPI.createClientGroup(groupForm);
      setShowAddGroup(false); setGroupForm({ org_id: '', name: '', description: '' });
      const gRes = await adminAPI.listClientGroups({});
      setGroups(gRes.data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) { console.error(e); }
  };

  const downloadTemplate = () => {
    const csv = 'name,email,password,client_group_id\nJohn Doe,john@example.com,password123,\nJane Smith,jane@example.com,password123,';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'client_upload_template.csv'; a.click();
  };

  const visibleGroups = groups.filter(g => (!orgFilter || g.org_id === orgFilter) && g.name.toLowerCase().includes(groupSearch.toLowerCase()));

  const toggleGroup = (gid) => {
    setSelectedGroups(prev => prev.includes(gid) ? prev.filter(x => x !== gid) : [...prev, gid]);
  };

  if (loading) return <AdminPageShell loading={true} />;

  return (
    <AdminPageShell
      title="Clients"
      subtitle="Manage clients across organizations and groups"
      testId="admin-clients-page"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAddGroup(true)} data-testid="add-group-button"><FolderOpen className="w-4 h-4 mr-1" />Add Group</Button>
          <Button variant="outline" size="sm" onClick={() => setShowBulk(true)} data-testid="bulk-upload-button"><Upload className="w-4 h-4 mr-1" />Bulk Upload</Button>
          <Button size="sm" onClick={openCreate} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="add-client-button"><UserPlus className="w-4 h-4 mr-1" />Add Client</Button>
        </div>
      }
    >

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-sm">
            <Search className="w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search by name, email, org..." value={search} onChange={e => setSearch(e.target.value)} data-testid="client-search-input" />
          </div>
          <Select value={orgFilter} onValueChange={v => { setOrgFilter(v === '_all' ? '' : v); setSelectedGroups([]); }}>
            <SelectTrigger className="w-52" data-testid="org-filter"><SelectValue placeholder="All Organizations" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All Organizations</SelectItem>
              {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>

          {/* Multi-select searchable group filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-52 justify-between" data-testid="group-filter-multi">
                <span className="truncate text-sm">{selectedGroups.length === 0 ? 'All Groups' : `${selectedGroups.length} group(s)`}</span>
                {selectedGroups.length > 0 && (
                  <X className="w-3.5 h-3.5 ml-1 shrink-0 opacity-50 hover:opacity-100" onClick={(e) => { e.stopPropagation(); setSelectedGroups([]); }} />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-60 p-2" align="start">
              <Input placeholder="Search groups..." value={groupSearch} onChange={e => setGroupSearch(e.target.value)} className="mb-2 h-8 text-sm" data-testid="group-search-input" />
              <div className="max-h-48 overflow-y-auto space-y-1">
                {visibleGroups.map(g => (
                  <label key={g.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer text-sm">
                    <Checkbox checked={selectedGroups.includes(g.id)} onCheckedChange={() => toggleGroup(g.id)} data-testid={`group-check-${g.id}`} />
                    <span className="truncate">{g.name}</span>
                    <Badge variant="secondary" className="text-[10px] px-1.5 ml-auto">{g.client_count || 0}</Badge>
                  </label>
                ))}
                {visibleGroups.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">No groups found</p>}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Clients Table */}
        <div className="rounded-lg border bg-card">
          <ScrollArea className="max-h-[600px]">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('name')} data-testid="sort-name">Name<SortIcon col="name" sortCol={sortCol} sortDir={sortDir} /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('email')} data-testid="sort-email">Email<SortIcon col="email" sortCol={sortCol} sortDir={sortDir} /></TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email Status</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('org')} data-testid="sort-org">Organization<SortIcon col="org" sortCol={sortCol} sortDir={sortDir} /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('group')} data-testid="sort-group">Group<SortIcon col="group" sortCol={sortCol} sortDir={sortDir} /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('joined')} data-testid="sort-joined">Joined<SortIcon col="joined" sortCol={sortCol} sortDir={sortDir} /></TableHead>
                <TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map(c => (
                  <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.email}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {c.phone ? (
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>
                      ) : <span className="text-xs text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell>
                      {c.email_validated ? (
                        <Badge variant="default" className="text-xs gap-1" data-testid={`email-validated-${c.id}`}>
                          <CheckCircle className="w-3 h-3" />Verified
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs gap-1" data-testid={`email-unvalidated-${c.id}`}>
                          <Mail className="w-3 h-3" />Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{c.org_name}</Badge></TableCell>
                    <TableCell className="text-sm">{c.client_group_name || '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}><Edit className="w-4 h-4" /></Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`client-actions-${c.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {!c.email_validated && (
                              <>
                                <DropdownMenuItem onClick={() => handleSendValidation(c.id)} data-testid={`send-validation-${c.id}`}>
                                  <Send className="w-4 h-4 mr-2" />Send Validation Email
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleManualValidate(c.id)} data-testid={`manual-validate-${c.id}`}>
                                  <ShieldCheck className="w-4 h-4 mr-2" />Manually Validate
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem onClick={() => openEdit(c)}>
                              <Edit className="w-4 h-4 mr-2" />Edit Client
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No clients found</TableCell></TableRow>}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>

        {/* Add/Edit Client Dialog */}
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent data-testid="add-client-dialog">
            <DialogHeader><DialogTitle>{editClient ? 'Edit Client' : 'Add Client'}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Full Name *</Label><Input value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} data-testid="add-client-name" placeholder="John Doe" /></div>
              <div className="space-y-2"><Label>Email *</Label><Input type="email" value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} data-testid="add-client-email" placeholder="john@example.com" /></div>
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="tel" className="pl-10" value={addForm.phone} onChange={e => setAddForm(p => ({ ...p, phone: e.target.value }))} data-testid="add-client-phone" placeholder="+1 (555) 123-4567" />
                </div>
              </div>
              {!editClient && <div className="space-y-2"><Label>Password *</Label><Input type="password" value={addForm.password} onChange={e => setAddForm(p => ({ ...p, password: e.target.value }))} data-testid="add-client-password" /></div>}
              <div className="space-y-2"><Label>Organization *</Label>
                <Select value={addForm.org_id} onValueChange={v => setAddForm(p => ({ ...p, org_id: v, client_group_id: '' }))}><SelectTrigger data-testid="add-client-org"><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>{orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>
              {addForm.org_id && groups.filter(g => g.org_id === addForm.org_id).length > 0 && (
                <div className="space-y-2"><Label>Client Group</Label>
                  <Select value={addForm.client_group_id} onValueChange={v => setAddForm(p => ({ ...p, client_group_id: v }))}><SelectTrigger data-testid="add-client-group"><SelectValue placeholder="Select group (optional)" /></SelectTrigger>
                    <SelectContent>{groups.filter(g => g.org_id === addForm.org_id).map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent></Select></div>
              )}
              {!editClient && (
                <div className="rounded-lg border p-3 bg-secondary/50 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                  <p className="text-xs text-muted-foreground">A verification email will be sent automatically upon creation.</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={!addForm.name || !addForm.email || (!editClient && !addForm.password) || !addForm.org_id || addLoading}
                className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-add-client">
                {addLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}{editClient ? 'Save' : 'Add Client'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={showBulk} onOpenChange={v => { setShowBulk(v); setBulkResult(null); }}>
          <DialogContent data-testid="bulk-upload-dialog">
            <DialogHeader><DialogTitle>Bulk Upload Clients</DialogTitle>
              <DialogDescription>Upload a CSV file with columns: name, email, password, client_group_id (optional)</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Organization *</Label>
                <Select value={bulkOrgId} onValueChange={setBulkOrgId}><SelectTrigger data-testid="bulk-org-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>{orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>
              {bulkOrgId && groups.filter(g => g.org_id === bulkOrgId).length > 0 && (
                <div className="space-y-2"><Label>Default Client Group</Label>
                  <Select value={bulkGroupId} onValueChange={setBulkGroupId}><SelectTrigger><SelectValue placeholder="Select group (optional)" /></SelectTrigger>
                    <SelectContent>{groups.filter(g => g.org_id === bulkOrgId).map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent></Select></div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template"><Download className="w-4 h-4 mr-1" />Download Template</Button>
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={!bulkOrgId || uploading} data-testid="upload-csv-button">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}Choose CSV
                </Button>
                <input ref={fileRef} type="file" className="hidden" accept=".csv" onChange={handleBulkUpload} />
              </div>
              {bulkResult && (
                <div className="rounded-lg border p-4 bg-secondary/50">
                  <p className="text-sm font-medium">Upload Result: {bulkResult.created} created</p>
                  {bulkResult.errors?.length > 0 && (
                    <div className="mt-2 text-sm text-destructive">
                      {bulkResult.errors.map((e, i) => <p key={i}>Row {e.row}: {e.error}</p>)}
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowBulk(false)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Group Dialog */}
        <Dialog open={showAddGroup} onOpenChange={setShowAddGroup}>
          <DialogContent data-testid="add-group-dialog">
            <DialogHeader><DialogTitle>Add Client Group</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Organization *</Label>
                <Select value={groupForm.org_id} onValueChange={v => setGroupForm(p => ({ ...p, org_id: v }))}><SelectTrigger><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>{orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Group Name *</Label><Input value={groupForm.name} onChange={e => setGroupForm(p => ({ ...p, name: e.target.value }))} placeholder="Litigation Team" data-testid="group-name-input" /></div>
              <div className="space-y-2"><Label>Description</Label><Input value={groupForm.description} onChange={e => setGroupForm(p => ({ ...p, description: e.target.value }))} placeholder="Description..." /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddGroup(false)}>Cancel</Button>
              <Button onClick={handleAddGroup} disabled={!groupForm.org_id || !groupForm.name} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-add-group">Create Group</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </AdminPageShell>
  );
}
